import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  Tag,
  Typography,
  notification,
} from 'antd';
import { useEffect, useState } from 'react';
import apiService from '../../services/api-service';

type Product = {
  id?: number;
  description?: string;
  code?: string;
  shortCode?: string;
  categoryId?: number;
  parentId?: number;
  minPrice?: number;
  maxPrice?: number;
  vat?: number;
  active?: boolean;
};

type ProductCategory = {
  id?: number;
  name?: string;
  description?: string;
  code?: string;
};

type ProductPageProps = {
  apiUrl?: string;
  title?: string;
};

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined) {
    return '-';
  }
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) {
    return '-';
  }
  return new Intl.NumberFormat('en-US').format(numberValue);
};

const parseCurrencyInput = (value?: string) => {
  if (!value) {
    return 0;
  }
  const digits = value.replace(/[^\d]/g, '');
  return Number(digits || 0);
};

export default function ProductPage(props: ProductPageProps) {
  const [data, setData] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [currentEditItem, setCurrentEditItem] = useState<Product>();
  const [isCreateNewItem, setIsCreateNewItem] = useState(false);
  const [form] = Form.useForm<Product>();

  async function getAllProducts() {
    const response = await apiService.get<Product[]>('/api/products');
    setData(response.data || []);
  }

  async function getCategories() {
    try {
      const response = await apiService.get<ProductCategory[]>('/api/products/categories');
      setCategories(response.data || []);
    } catch (error: any) {
      notification.error({ message: 'Lỗi tải danh mục sản phẩm' });
      console.error(error?.message);
    }
  }

  const onEditItem = (item: Product) => {
    setCurrentEditItem(item);
    form.setFieldsValue(item);
  };

  const onPressCreateNewItem = () => {
    setIsCreateNewItem(true);
    form.setFieldsValue({ active: true });
  };

  const onCreateNewItem = async () => {
    try {
      const submitData = form.getFieldsValue();
      const response = await apiService.post('/api/products', submitData);
      if (response.status == 200) {
        notification.success({ message: 'Thêm sản phẩm thành công' });
      }
      form.resetFields();
      setIsCreateNewItem(false);
      getAllProducts();
    } catch (error: any) {
      notification.error({ message: 'Lỗi thêm mới sản phẩm' });
      console.error(error?.message);
    }
  };

  const onUpdateItem = async () => {
    try {
      const submitData: Product = form.getFieldsValue();
      submitData.id = currentEditItem!!.id;
      const response = await apiService.put(`/api/products/${currentEditItem!!.id}`, submitData);
      if (response.status == 200) {
        notification.success({ message: 'Cập nhật sản phẩm thành công' });
      }
      onCloseModal();
      getAllProducts();
    } catch (error: any) {
      notification.error({ message: 'Lỗi cập nhật sản phẩm' });
      console.error(error?.message);
    }
  };

  const onCloseModal = () => {
    setCurrentEditItem(undefined);
    setIsCreateNewItem(false);
    form.resetFields();
  };

  useEffect(() => {
    getAllProducts();
    getCategories();
  }, []);

  return (
    <div className="p-[24px]">
      <Typography.Title level={4}>{props.title || 'Quản lý: Danh sách sản phẩm'}</Typography.Title>
      <div className="mb-[16px]">
        <Button size="large" onClick={onPressCreateNewItem}>
          Tạo mới
        </Button>
      </div>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Table
            dataSource={data}
            rowKey="id"
            columns={[
              {
                title: '#ID',
                key: 'id',
                dataIndex: 'id',
              },
              {
                title: 'SKU',
                key: 'code',
                dataIndex: 'code',
              },
              {
                title: 'Short SKU',
                key: 'shortCode',
                dataIndex: 'shortCode',
              },
              {
                title: 'Mô tả',
                key: 'description',
                dataIndex: 'description',
              },
              {
                title: 'Danh mục',
                key: 'categoryId',
                dataIndex: 'categoryId',
                render: (categoryId: Product['categoryId']) => {
                  if (!categoryId) {
                    return '-';
                  }
                  const category = categories.find(item => item.id === categoryId);
                  return category?.name || category?.code || category?.description || '-';
                },
              },
              {
                title: 'Danh mục cha',
                key: 'parentId',
                dataIndex: 'parentId',
                render: (parentId: Product['parentId']) => {
                  if (!parentId) {
                    return '-';
                  }
                  const parent = data.find(item => item.id === parentId);
                  return parent?.description || parent?.code || parent?.shortCode || '-';
                },
              },
              {
                title: 'VAT',
                key: 'vat',
                dataIndex: 'vat',
              },
              {
                title: 'Giá tối thiểu',
                key: 'minPrice',
                dataIndex: 'minPrice',
                render: (value: Product['minPrice']) => formatCurrency(value),
              },
              {
                title: 'Giá tối đa',
                key: 'maxPrice',
                dataIndex: 'maxPrice',
                render: (value: Product['maxPrice']) => formatCurrency(value),
              },
              {
                title: 'Kích hoạt',
                key: 'active',
                dataIndex: 'active',
                render: (value: Product['active']) => (
                  <Tag color={value ? 'green' : 'red'}>{value ? 'Active' : 'Deactive'}</Tag>
                ),
              },
            ]}
            onRow={record => {
              return {
                onClick: () => onEditItem(record),
              };
            }}
          />
        </Col>
      </Row>
      <Modal
        open={!!currentEditItem || isCreateNewItem}
        destroyOnClose={true}
        cancelText="Hủy"
        okText={isCreateNewItem ? 'Lưu' : 'Cập nhật'}
        title={isCreateNewItem ? 'Tạo mới sản phẩm' : 'Cập nhật sản phẩm'}
        onCancel={() => onCloseModal()}
        onOk={isCreateNewItem ? onCreateNewItem : onUpdateItem}
        width="50vw"
        styles={{
          content: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 48px)',
            overflow: 'hidden',
          },
          body: {
            flex: 1,
            overflowY: 'auto',
            padding: 16,
          },
        }}
      >
        <Form form={form} layout="horizontal" labelAlign="left" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
          <Form.Item label="ID" name="id">
            <InputNumber className="w-full" disabled />
          </Form.Item>
          <Form.Item label="Mô tả" name="description">
            <Input />
          </Form.Item>
          <Form.Item label="SKU" name="code">
            <Input />
          </Form.Item>
          <Form.Item label="Short SKU" name="shortCode">
            <Input />
          </Form.Item>
          <Form.Item label="Danh mục" name="categoryId">
            <Select
              allowClear
              showSearch
              className="w-full"
              optionFilterProp="label"
              options={categories.map(category => ({
                value: category.id,
                label: category.name || category.code || category.description || `${category.id ?? ''}`,
              }))}
            />
          </Form.Item>
          <Form.Item label="Sản phẩm gốc" name="parentId">
            <Select
              allowClear
              showSearch
              className="w-full"
              optionFilterProp="label"
              options={data.map(product => ({
                value: product.id,
                label: product.description || product.code || product.shortCode || `${product.id ?? ''}`,
              }))}
            />
          </Form.Item>
          <Form.Item label="VAT" name="vat">
            <Select
              className="w-full"
              options={[
                { value: 0, label: '0%' },
                { value: 8, label: '8%' },
                { value: 10, label: '10%' },
              ]}
            />
          </Form.Item>
          <Form.Item label="Giá tối thiểu" name="minPrice">
            <InputNumber
              className="w-full"
              min={0}
              formatter={value => (value ? formatCurrency(Number(value)) : '')}
              parser={parseCurrencyInput}
            />
          </Form.Item>
          <Form.Item label="Giá tối đa" name="maxPrice">
            <InputNumber
              className="w-full"
              min={0}
              formatter={value => (value ? formatCurrency(Number(value)) : '')}
              parser={parseCurrencyInput}
            />
          </Form.Item>
          <Form.Item label="Kích hoạt" name="active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
